import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faBox,
  faPlus,
  faUser,
  faBell,
  faBars,
  faTimes,
  faSignOutAlt,
  faWallet,
  faSearchLocation,
  faArrowLeft,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTruck,
  faEdit,
  faTrash,
  faEye,
  faMapMarkerAlt,
  faClock,
  faUndo,
  faChartLine,
  faUsers,
  faCog,
  faHome,
  faChevronRight,
  faPenToSquare
} from '@fortawesome/free-solid-svg-icons';

// Import agent components
import UpdateTracking from './Shipments/UpdateTracking';
import { shipmentService } from '@/services/shipmentService';
import { userService } from '@/services/userService';
import useWelcomeOnce from '@/hooks/useWelcomeOnce';
import { useUIStore } from '@/store/uiStore';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import UserSearch from '@/components/common/UserSearch';

export const AgentDashboardHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pendingShipments: 0,
    inTransit: 0,
    delivered: 0,
    totalShipments: 0,
    todayDeliveryAmount: 0,
    deliveredToday: 0
  });
  const [user, setUser] = useState(null);
  const [updatesByYou, setUpdatesByYou] = useState({ today: 0, allTime: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const [shipmentStats, userData, agentUpdates] = await Promise.all([
          shipmentService.getStats(token),
          userService.getMe().catch(() => null),
          shipmentService.getAgentUpdateStats(token, 'today').catch(() => ({ today: 0, allTime: 0 }))
        ]);

        if (userData) setUser(userData.user);

        setStats({
          pendingShipments: shipmentStats.pendingCount || 0,
          inTransit: shipmentStats.inTransitCount || 0,
          delivered: shipmentStats.deliveredCount || 0,
          totalShipments: shipmentStats.totalShipments || 0,
          todayDeliveryAmount: shipmentStats.todayDeliveryAmount || 0,
          deliveredToday: shipmentStats.deliveredToday || 0
        });
        setUpdatesByYou({
          today: agentUpdates.today || 0,
          allTime: agentUpdates.allTime || 0
        });
      } catch (err) {
        console.error('Failed to fetch dashboard data', err);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const displayName = user?.name || JSON.parse(localStorage.getItem('user') || '{}').name || 'Agent';
  const centerAddress = user?.centerAddress || JSON.parse(localStorage.getItem('user') || '{}').centerAddress || '';

  return (
    <div>
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Agent Dashboard</h1>
          <p className="text-gray-600">Welcome back, {displayName}. Here's what's happening with your shipments today.</p>
        </div>
        {centerAddress && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-start gap-3 max-w-md">
            <div className="mt-1">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600" />
            </div>
            <div>
              <p className="text-xs font-bold text-red-800 uppercase tracking-wider mb-1">Your Pickup Center</p>
              <p className="text-sm text-red-700 leading-tight">{centerAddress}</p>
            </div>
          </div>
        )}
      </div>

      {/* Today's Overview */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-black via-neutral-800 to-red-600 text-white rounded-2xl shadow-xl p-6 md:p-7">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <FontAwesomeIcon icon={faWallet} className="text-white text-xl" />
              </div>
              <div>
                <p className="text-sm opacity-90">Today Delivery Amount</p>
                <p className="text-3xl md:text-4xl font-extrabold leading-tight">₦{Number(stats.todayDeliveryAmount || 0).toLocaleString()}</p>
                <p className="text-xs md:text-sm mt-1 opacity-90">Based on today's created shipments</p>
              </div>
            </div>
            <div className="flex-1 bg-white/10 rounded-xl p-4 lg:bg-transparent lg:p-0">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:flex lg:items-center lg:justify-around lg:divide-x lg:divide-white/20 gap-4 lg:gap-0">
                <div className="lg:px-6 text-center lg:text-left">
                  <p className="text-xs text-gray-300 uppercase font-semibold tracking-wider mb-1">Deliveries Today</p>
                  <p className="text-3xl font-bold">{stats.deliveredToday}</p>
                </div>
                <div className="lg:px-6 text-center lg:text-left">
                  <p className="text-xs text-gray-300 uppercase font-semibold tracking-wider mb-1">Updated by You</p>
                  <p className="text-3xl font-bold">{updatesByYou.today}</p>
                </div>
                <div className="hidden md:block lg:px-6 text-center lg:text-left">
                  <p className="text-xs text-gray-300 uppercase font-semibold tracking-wider mb-1">All‑time Updates</p>
                  <p className="text-3xl font-bold">{updatesByYou.allTime}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap lg:flex-nowrap gap-3">
              <button
                onClick={() => navigate('/dashboard/agent/shipments')}
                className="inline-flex items-center px-4 py-2 bg-white/20 text-white border border-white/30 rounded-lg hover:bg-white/30 transition-colors"
                aria-label="Go to All Shipments"
              >
                <FontAwesomeIcon icon={faBox} className="mr-2" /> All Shipments
              </button>
              <button
                onClick={() => navigate('/dashboard/agent/tracking')}
                className="inline-flex items-center px-4 py-2 bg-white/10 text-white border border-white/10 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Go to Update Tracking"
              >
                <FontAwesomeIcon icon={faPenToSquare} className="mr-2" /> Update Tracking
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* User Lookup Section */}
      <UserSearch />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow p-6 border-l-4 border-red-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Pending Shipments</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.pendingShipments}</p>
            </div>
            <div className="p-3 rounded-full bg-red-100 text-red-600">
              <FontAwesomeIcon icon={faClock} className="text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 border-l-4 border-red-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">In Transit</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.inTransit}</p>
            </div>
            <div className="p-3 rounded-full bg-red-100 text-red-600">
              <FontAwesomeIcon icon={faTruck} className="text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 border-l-4 border-red-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Delivered</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.delivered}</p>
            </div>
            <div className="p-3 rounded-full bg-red-100 text-red-600">
              <FontAwesomeIcon icon={faCheckCircle} className="text-xl" />
            </div>
          </div>
        </div>

        {/* Removed extra card to keep focus on brand-consistent trio */}
      </div>
    </div>
  );
};

const AgentDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  useWelcomeOnce('agent_dashboard', 'Welcome! 🚛 Update tracking or book new shipments.');
  const { pushToast } = useUIStore();
  const statusMapRef = useRef({});

  // Check user session on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const me = await userService.getMe();
        if (me) {
          setUser(me);
        } else {
          // Fallback to local storage
          const localUser = localStorage.getItem('user');
          if (localUser) setUser(JSON.parse(localUser));
        }
      } catch (e) {
        console.warn('Session check failed, using local fallback', e);
        const localUser = localStorage.getItem('user');
        if (localUser) setUser(JSON.parse(localUser));
      } finally {
        setSessionChecked(true);
      }
    };

    checkSession();
  }, []);

  // Real-time shipment status updates for agents
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const es = shipmentService.subscribeStream(token);
    es.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data || '{}');
        const shipments = payload?.shipments || payload || [];
        shipments.forEach((s) => {
          const id = s.trackingCode || s._id || s.id;
          const curr = (s.status || '').trim();
          const prev = statusMapRef.current[id];
          if (!prev) {
            statusMapRef.current[id] = curr;
            return;
          }
          if (prev !== curr) {
            statusMapRef.current[id] = curr;
            const type = curr.toLowerCase() === 'delivered' ? 'success' : 'info';
            pushToast(`Shipment ${id} status changed to ${curr}`, type, 2500, { ariaLabel: 'Shipment status update' });
          }
        });
      } catch (e) {
        console.warn('Agent stream parse error', e);
      }
    };
    return () => { try { es.close(); } catch { } };
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Function to handle sign out
  const handleSignOut = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    navigate('/auth');
  };

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/shipments') && !path.includes('/tracking')) return 'shipments';
    if (path.includes('/tracking')) return 'tracking';
    if (path.includes('/book-shipment')) return 'book-shipment';
    if (path.includes('/profile')) return 'profile';
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  // Get tab title for navbar
  const getTabTitle = () => {
    switch (activeTab) {
      case 'dashboard': return 'Dashboard';
      case 'shipments': return 'All Shipments';
      case 'tracking': return 'Update Tracking';
      case 'book-shipment': return 'Book Shipment';
      case 'profile': return 'My Profile';
      default: return 'Dashboard';
    }
  };


  // Show loading while checking session
  if (!sessionChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // If user is not logged in, redirect to login page
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-2xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Session Expired</h2>
            <p className="text-gray-600 mt-2">Please log in again to continue</p>
          </div>
          <Link
            to="/auth"
            className="w-full py-3 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 inline-block text-center transition-colors duration-300"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Navbar */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={toggleSidebar}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 focus:outline-none transition-colors duration-300 hover:bg-gray-200"
          >
            <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} className="text-xl" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">{getTabTitle()}</h1>
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 text-red-600">
            <FontAwesomeIcon icon={faUser} className="text-lg" />
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300" onClick={toggleSidebar}></div>
          <div className="fixed left-0 top-0 bottom-0 w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <FontAwesomeIcon icon={faUser} className="text-red-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{user.name}</h3>
                  <p className="text-sm text-gray-600">Agent: {user.name}</p>
                </div>
              </div>
            </div>
            <nav className="p-4">
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/dashboard/agent"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'dashboard' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <FontAwesomeIcon icon={faTachometerAlt} className="mr-3 text-gray-500" />
                    Dashboard
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/agent/shipments"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'shipments' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <FontAwesomeIcon icon={faBox} className="mr-3 text-gray-500" />
                    All Shipments
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/agent/tracking"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'tracking' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-3 text-gray-500" />
                    Update Tracking
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/agent/profile"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'profile' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-3 text-gray-500" />
                    My Profile
                  </Link>
                </li>
                <li className="pt-4 mt-4 border-t border-gray-200">
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-3 rounded-lg flex items-center text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="mr-3 text-gray-500" />
                    Sign Out
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Desktop Header */}
      <div className="hidden md:flex items-center justify-between bg-white shadow-sm py-4 px-6 mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center mr-4">
            <FontAwesomeIcon icon={faHome} className="text-white text-xl" />
          </div>
          <h1 className="text-xl font-bold text-gray-800">Fex Logistics</h1>
        </div>

        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="font-medium text-gray-800">{user.name}</p>
            <p className="text-sm text-gray-600">Agent: {user.name}</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faUser} className="text-red-600" />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 pt-24 md:pt-6">
        <div className="flex flex-col md:flex-row">
          {/* Desktop Sidebar */}
          <div className="hidden md:block md:w-64 mb-6 md:mb-0 md:mr-6">
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-6">
              <div className="mb-6 p-4 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faUser} className="text-red-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{user.name}</h3>
                    <p className="text-sm text-gray-600">Agent: {user.name}</p>
                  </div>
                </div>
              </div>
              <nav>
                <ul className="space-y-1">
                  <li>
                    <Link
                      to="/dashboard/agent"
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'dashboard' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      <FontAwesomeIcon icon={faTachometerAlt} className="mr-3 text-gray-500" />
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/agent/shipments"
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'shipments' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      <FontAwesomeIcon icon={faBox} className="mr-3 text-gray-500" />
                      All Shipments
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/agent/tracking"
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'tracking' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-3 text-gray-500" />
                      Update Tracking
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/agent/book-shipment"
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'book-shipment' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-3 text-gray-500" />
                      Book Shipment
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/agent/profile"
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors duration-200 ${activeTab === 'profile' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      <FontAwesomeIcon icon={faUser} className="mr-3 text-gray-500" />
                      My Profile
                    </Link>
                  </li>
                </ul>
              </nav>

              <div className="mt-8 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-3 rounded-lg flex items-center text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="mr-3 text-gray-500" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-800">{getTabTitle()}</h1>
            </div>
            {activeTab === 'shipments' && (
              <div className="mb-4">
                <button
                  onClick={() => navigate('/dashboard/agent/book-shipment')}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Book Shipment
                </button>
              </div>
            )}

            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
